import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Factor } from '@supabase/supabase-js';
import { supabase, type Zugang } from '../lib/supabase';
import { useSession } from '../lib/session';
import { t } from '../lib/sprache';
import { schluesselGruppiert, TOTP_AUSSTELLER, totpDetails, totpQrCode } from '../lib/totp';
import { Check, Copy, Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import { aktuelleRpId, mitPasskeyBestaetigen, passkeysMoeglich } from '../lib/passkey';
import { geraetMerken } from '../lib/mfaGeraet';


/**
 * Schranke vor den Geschäftsdaten: erscheint, wenn die Zugriffsrichtlinie
 * des Mandanten nicht erfüllt ist. Die Datenbank erzwingt die Regeln ohnehin;
 * diese Seite sagt dem Benutzer, was zu tun ist.
 */
export default function ZugangSchranke({ zugang, mandantName, mandantKurz }: { zugang: Zugang; mandantName: string; mandantKurz?: string }) {
  const { abmelden } = useSession();

  if (zugang.grund === 'mfa') return <MfaSchritt mandantName={mandantName} mandantKurz={mandantKurz} erneut={zugang.mfa_erneut === true} merkenStunden={zugang.mfa_merken_stunden ?? null} passkey={zugang.passkey_erlaubt !== false && (zugang.passkey_rps ?? []).includes(aktuelleRpId()) && passkeysMoeglich()} />;

  let titel = t('Kein Zugriff');
  let text = t('Der Zugriff auf diesen Mandanten ist derzeit nicht möglich.');
  if (zugang.grund === 'sitzung') {
    titel = t('Sitzung abgelaufen');
    text = t('Die maximale Sitzungsdauer für {mandant} ist erreicht. Bitte melde dich erneut an.', { mandant: mandantName });
  } else if (zugang.grund === 'ip') {
    titel = t('Netzwerk nicht freigegeben');
    text = t('{mandant} erlaubt den Zugriff nur aus freigegebenen Netzwerken. Deine aktuelle Adresse ist {ip}. Bitte wende dich an deinen Administrator.', { mandant: mandantName, ip: zugang.ip ?? t('unbekannt') });
  } else if (zugang.grund === 'fehler') {
    text = t('Der Zugriffsstatus konnte nicht geprüft werden. Bitte lade die Seite neu.');
  }

  return (
    <main className="zentriert">
      <div className="karte anmelde-karte" data-tour="zugang-schranke">
        <h1>{titel}</h1>
        <p className="leise">{text}</p>
        <button className="kn kn-primaer" onClick={abmelden}>{t('Neu anmelden')}</button>
      </div>
    </main>
  );
}

type Phase = 'laden' | 'einrichten' | 'bestaetigen';

function MfaSchritt({ mandantName, mandantKurz, erneut, merkenStunden, passkey }: { mandantName: string; mandantKurz?: string; erneut?: boolean; merkenStunden: number | null; passkey?: boolean }) {
  const [passkeyLaeuft, setPasskeyLaeuft] = useState(false);
  const { abmelden, zugangPruefen } = useSession();
  const [phase, setPhase] = useState<Phase>('laden');
  const [faktorId, setFaktorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [details, setDetails] = useState<{ aussteller: string; konto: string; schluessel: string } | null>(null);
  const [manuell, setManuell] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [code, setCode] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const gestartet = useRef(false);

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    void vorbereiten();
  }, []);

  async function vorbereiten() {
    setFehler(null);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) return setFehler(t('Die Anmeldung konnte nicht vorbereitet werden. Bitte laden Sie die Seite neu.'));
    const alle: Factor[] = data?.all ?? [];
    const bestaetigt = alle.find((f) => f.factor_type === 'totp' && f.status === 'verified');
    if (bestaetigt) {
      setFaktorId(bestaetigt.id);
      setPhase('bestaetigen');
      return;
    }
    // Abgebrochene Einrichtungen entfernen, sonst lehnt Supabase eine neue ab
    for (const f of alle.filter((x) => x.factor_type === 'totp' && x.status !== 'verified')) {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
    // Kurzname, damit der Eintrag in der Authenticator-App vollständig lesbar bleibt (z. B. "merkan | LMS")
    const aussteller = TOTP_AUSSTELLER.mandant(mandantKurz || mandantName);
    const neu = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Merkan', issuer: aussteller });
    if (neu.error) return setFehler(t('Die Einrichtung konnte nicht gestartet werden. Bitte laden Sie die Seite neu.'));
    setFaktorId(neu.data.id);
    setQr(await totpQrCode(neu.data.totp.uri).catch(() => neu.data.totp.qr_code));
    const d = totpDetails(neu.data.totp.uri);
    setDetails({ aussteller: d.aussteller || aussteller, konto: d.konto, schluessel: neu.data.totp.secret || d.schluessel });
    setPhase('einrichten');
  }

  async function pruefen(e: FormEvent) {
    e.preventDefault();
    if (!faktorId || code.length !== 6) return;
    setLaeuft(true);
    setFehler(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: faktorId, code });
    setLaeuft(false);
    if (error) {
      setCode('');
      return setFehler(t('Der Code ist ungültig oder abgelaufen. Bitte geben Sie den aktuellen Code ein.'));
    }
    // Bestätigung auf diesem Gerät merken, Dauer steuert die Richtlinie im Admin Center
    if (merkenStunden) await geraetMerken();
    await zugangPruefen();
  }

  async function passkeyBestaetigen() {
    setFehler(null);
    setPasskeyLaeuft(true);
    const r = await mitPasskeyBestaetigen();
    if (!r.ok) {
      setPasskeyLaeuft(false);
      if (!r.abgebrochen) setFehler(r.fehler);
      return;
    }
    // Bestätigung auf diesem Gerät merken, Dauer steuert die Richtlinie im Admin Center
    if (merkenStunden) await geraetMerken();
    await zugangPruefen();
    setPasskeyLaeuft(false);
  }

  async function kopieren() {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(details.schluessel.replace(/\s/g, ''));
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 1600);
    } catch {
      /* Zwischenablage nicht verfügbar, Schlüssel bleibt sichtbar */
    }
  }

  const codeFeld = (
    <label className="feld">
      <span>{t('Sechsstelliger Code')}</span>
      <input
        className="eingabe code-eingabe"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        autoFocus={phase === 'bestaetigen'}
        required
        placeholder="000000"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        aria-label={t('Sechsstelliger Code')}
      />
    </label>
  );

  return (
    <main className="zentriert">
      <form className={`karte anmelde-karte mfa-karte${phase === 'einrichten' ? ' mfa-karte-breit' : ''}`} onSubmit={pruefen} data-tour="zugang-mfa">
        <div className="mfa-kopf">
          <span className="mfa-symbol" aria-hidden="true"><ShieldCheck size={20} /></span>
          <span className="mfa-eyebrow">{t('Sicherheit für Ihr Konto')}</span>
        </div>

        {phase === 'laden' && (
          <>
            <h1>{t('Einen Moment bitte')}</h1>
            {!fehler && <p className="leise">{t('Die sichere Anmeldung wird vorbereitet …')}</p>}
          </>
        )}

        {phase === 'einrichten' && (
          <>
            <h1>{t('Zwei-Faktor-Anmeldung einrichten')}</h1>
            <p className="leise mfa-einleitung">
              {t('Schützen Sie Ihren Merkan-Zugang für {mandant} mit einem zweiten Faktor. Lassen Sie uns die mehrstufige Anmeldung (MFA) jetzt gemeinsam einrichten, es dauert weniger als eine Minute.', { mandant: mandantName })}
            </p>

            <ol className="mfa-schritte">
              <li><span>1</span>{t('Öffnen Sie eine Authenticator-App, zum Beispiel Microsoft Authenticator oder Google Authenticator.')}</li>
              <li><span>2</span>{t('Scannen Sie den QR-Code mit der App.')}</li>
              <li><span>3</span>{t('Geben Sie den sechsstelligen Code aus der App unten ein.')}</li>
            </ol>

            <div className="mfa-qr-flaeche">
              {qr && !manuell && <img className="mfa-qr" src={qr} alt={t('QR-Code für die Authenticator-App')} width={216} height={216} draggable={false} />}
              {manuell && details && (
                <div className="mfa-manuell">
                  <div className="mfa-manuell-titel"><KeyRound size={16} aria-hidden="true" />{t('Konto manuell hinzufügen')}</div>
                  <dl>
                    <dt>{t('Konto')}</dt>
                    <dd>{details.aussteller}</dd>
                    {details.konto && <><dt>{t('Benutzer')}</dt><dd>{details.konto}</dd></>}
                    <dt>{t('Schlüssel')}</dt>
                    <dd className="mfa-schluessel">
                      <code>{schluesselGruppiert(details.schluessel)}</code>
                      <button type="button" className="mfa-kopieren" onClick={kopieren} aria-label={t('Schlüssel kopieren')}>
                        {kopiert ? <Check size={15} /> : <Copy size={15} />}
                        <span>{kopiert ? t('Kopiert') : t('Kopieren')}</span>
                      </button>
                    </dd>
                    <dt>{t('Art')}</dt>
                    <dd>{t('Zeitbasiert (TOTP), 6 Ziffern, 30 Sekunden')}</dd>
                  </dl>
                </div>
              )}
            </div>
            {details && (
              <button type="button" className="mfa-umschalten" onClick={() => setManuell((m) => !m)}>
                {manuell ? t('QR-Code wieder anzeigen') : t('Scannen nicht möglich? Manuell hinzufügen')}
              </button>
            )}
            {codeFeld}
          </>
        )}

        {phase === 'bestaetigen' && (
          <>
            <h1>{erneut ? t('Identität erneut bestätigen') : t('Anmeldung bestätigen')}</h1>
            <p className="leise">
              {erneut
                ? t('Zum Schutz Ihrer Daten verlangt {mandant} in regelmäßigen Abständen eine erneute Bestätigung. Geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein, danach arbeiten Sie direkt weiter.', { mandant: mandantName })
                : t('Geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein, um Ihren Merkan-Zugang für {mandant} zu öffnen.', { mandant: mandantName })}
            </p>
            {passkey && (
              <>
                <button type="button" className="kn kn-primaer mfa-passkey" onClick={() => void passkeyBestaetigen()} disabled={passkeyLaeuft || laeuft}>
                  <Fingerprint size={18} aria-hidden="true" />
                  {passkeyLaeuft ? t('Warte auf Bestätigung …') : t('Mit Passkey bestätigen')}
                </button>
                <div className="mfa-oder"><span>{t('oder Code eingeben')}</span></div>
              </>
            )}
            {codeFeld}
          </>
        )}

        {fehler && <p className="fehler">{fehler}</p>}
        {phase !== 'laden' && (
          <button className={passkey && phase === 'bestaetigen' ? 'kn' : 'kn kn-primaer'} type="submit" disabled={code.length !== 6 || laeuft}>
            {laeuft ? t('Wird geprüft …') : phase === 'einrichten' ? t('Einrichtung abschließen') : t('Bestätigen')}
          </button>
        )}
        <button type="button" className="leise-knopf" onClick={abmelden}>{t('Abmelden')}</button>
      </form>
    </main>
  );
}
